import type { Country } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import type { IdentificationTypes, KybFile } from '~/api/kyb/types'

import { KYB_UPLOAD_ENDPOINTS } from './useKybFileUpload.constants'
import type {
  KybUploadEndpoint,
  KybUploadEndpointWithPathParams,
  KybUploadEndpointWithoutPathParams,
} from './useKybFileUpload.types'

export function useKybFileUpload() {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  /**
   * Generic file upload function using multipart/form-data (without path params)
   */
  async function uploadFiles(
    endpoint: KybUploadEndpointWithoutPathParams,
    files: Record<string, File>,
    options?: {
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile>

  /**
   * Generic file upload function using multipart/form-data (with path params)
   */
  async function uploadFiles(
    endpoint: KybUploadEndpointWithPathParams,
    files: Record<string, File>,
    options: {
      pathParams: { id: string }
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile[]>

  /**
   * Implementation
   */
  async function uploadFiles(
    endpoint: KybUploadEndpoint,
    files: Record<string, File>,
    options?: {
      pathParams?: Record<string, string>
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile | KybFile[]> {
    const fileList = Object.entries(files)
    try {
      const formData = new FormData()
      fileList.forEach(([field, file]) => {
        formData.append(field, file)
      })

      // Add any additional form data if needed (e.g., metadata, document type)
      if (options?.additionalFormData) {
        Object.entries(options.additionalFormData).forEach(([key, value]) => {
          formData.append(key, value)
        })
      }

      return $httpFetch(endpoint, {
        method: 'POST',
        body: formData,
        pathParams: options?.pathParams as Record<string, string>,
      })
    } catch (error) {
      const fileNames = fileList.map(([, { name }]) => name).join(', ')
      const fileTypes = fileList.map(([, { type }]) => type).join(', ')
      const totalFileSize = fileList.reduce(
        (total, [, { size }]) => total + size,
        0,
      )
      logger.error(`Failed to upload file(s): ${fileNames} to ${endpoint}`, {
        error: error instanceof Error ? error : undefined,
        fileSize: totalFileSize,
        fileType: fileTypes,
        owners: ['bot-squad-seller-experience-front'],
      })

      throw error
    }
  }

  function uploadProofOfSecondHandDealerLicence(file: File) {
    return uploadFiles(
      KYB_UPLOAD_ENDPOINTS.PROOF_OF_SECOND_HAND_DEALER_LICENCE,
      { file },
    )
  }

  function uploadArticleOfIncorporation(file: File) {
    return uploadFiles(KYB_UPLOAD_ENDPOINTS.ARTICLE_OF_INCORPORATION, { file })
  }

  function uploadW9(file: File) {
    return uploadFiles(KYB_UPLOAD_ENDPOINTS.W9, { file })
  }

  function uploadIdentityDocument(
    stakeholderId: string,
    {
      back,
      country,
      documentType,
      front,
    }: {
      back?: File
      country: Country
      documentType: IdentificationTypes
      front: File
    },
  ): Promise<KybFile[]> {
    const files = { front }
    if (back) {
      Object.assign(files, { back })
    }

    return uploadFiles(KYB_UPLOAD_ENDPOINTS.PROOF_OF_IDENTITY, files, {
      pathParams: { id: stakeholderId },
      additionalFormData: {
        country,
        document_type: documentType,
      },
    })
  }

  function uploadLetterOfAuthorization(file: File) {
    return uploadFiles(KYB_UPLOAD_ENDPOINTS.LETTER_OF_AUTHORIZATION, { file })
  }

  return {
    uploadProofOfSecondHandDealerLicence,
    uploadArticleOfIncorporation,
    uploadW9,
    uploadIdentityDocument,
    uploadLetterOfAuthorization,
  }
}
